import React, { useEffect, useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Stack,
  Box,
  IconButton,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import AsyncSelect from "react-select/async";

// Components & APIs
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { UserContext } from "App";
import AppointmentsApi from "../../../../Classes/API/Appointments/AppointmentsApi";
import UsersApi from "../../../../Classes/API/UsersApi";
import { loadPartitions, loadBuildings } from "../../../../utils/buildingPartitionHelpers";
import LinkAccSessToAppHourReq from "../../../../Classes/Models/Requests/Appointments/LinkAccSessToAppHourReq";
import SearchUserReq from "../../../../Classes/Models/Requests/Users/SearchUser";
import SingleSessionsApps from "./SingleSessionsApps";

// Generic Selectors
import PatientSelector from "../../../../components/Selector/PatientSelector";
import SessionSelector from "../../../../components/Selector/SessionSelector";
import { sendAppointmentWhatsapp } from "../../../../utils/whatsappHelpers";

const CreateAppointmentPopUp = ({
  GetAppDayById,
  AppDay,
  AppHour,
  ShowCreateAppointment,
  EnableModal,
}) => {
  const User = useContext(UserContext);

  // States
  const [AccSession, SetAccSession] = useState({});
  const [partition, setPartition] = useState(null);
  const [building, setBuilding] = useState(null);
  const [isDummy, setIsDummy] = useState(false);
  const [dummyData, setDummyData] = useState({
    patientName: "",
    sessionName: "",
    patientId: null,
    patientObj: null,
  });
  const [loading, setLoading] = useState(false);
  const [createIsReady, setCreateIsReady] = useState(false);

  // --- Swal Config for High Z-Index ---
  const swalConfig = {
    target: "body",
    customClass: {
      container: "swal2-popup-high-z",
    },
  };

  if (!AppHour) return null;

  const handleClose = () => {
    setPartition(null); // Reset partition on close to match old code behavior
    EnableModal(false, {});
  };



  const handleCreate = async () => {
    if (
      !isDummy &&
      AccSession.isAdmitted === true &&
      User.userType !== "Admin"
    ) {
      await Swal.fire({
        ...swalConfig,
        title: "Not Allowed",
        text: "This session is already admitted. Contact admin to change.",
        icon: "error",
      });
      return;
    }

    if (!partition) {
      const result = await Swal.fire({
        ...swalConfig,
        title: "Warning",
        text: "No partition was selected. Do you want to continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Book Appointment",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;
    }
    setLoading(true);

    const pId = isDummy ? dummyData.patientId : AccSession.patientId;
    const pName = isDummy ? dummyData.patientName : AccSession.patientName;
    const sName = isDummy ? dummyData.sessionName : AccSession.sessionName;

    const requestBody = {
      AccountingSessionId: isDummy ? 0 : AccSession.id,
      AppointmentHourId: AppHour.id,
      IsDummy: isDummy,
      PartitionId: partition?.value || null,
      BuildingId: building?.value || null,
      PatientId: pId || 0,
      PatientName: pName,
      SessionName: sName,
    };

    Swal.fire({
      ...swalConfig,
      title: "Processing",
      text: "Please wait...",
      didOpen: () => Swal.showLoading(),
      icon: "info",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
    });

    try {
      let res =
        await AppointmentsApi.LinkAccountingSessionToAppointmentHour(
          requestBody,
        );
      setLoading(false);

      if (res.status === "Success") {
        GetAppDayById(AppDay.id);
        handleClose();

        const allowedUsers = [
          "SuperAdmin",
          "Admin",
          "AppointmentEmployee",
          "AccountingEmployee",
          "CallCenterEmployee",
        ];
        const canSendWhatsapp = allowedUsers.includes(User.userType);

        const result = await Swal.fire({
          ...swalConfig,
          title: "Success",
          text: `Appointment created successfully -- ${pName}, ${sName}, with ${AppHour.therapistName} at ${new Date(AppHour.appointmentHourTime).toLocaleString()}`,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Send WhatsApp",
          cancelButtonText: "Close",
          showConfirmButton: canSendWhatsapp,
          confirmButtonColor: "#058a00",
          cancelButtonColor: "#3560AD",
        });

        if (result.isConfirmed && canSendWhatsapp) {
          sendAppointmentWhatsapp({
            patientId: pId,
            AppHour: AppHour,
            isDummy: isDummy,
            pName: pName,
            sName: sName,
            swalConfig: swalConfig,
          });
        }

        handleClose(); // Close modal after success
      } else {
        Swal.fire({
          ...swalConfig,
          title: "Failed",
          text: res.message,
          icon: "error",
        });
      }
    } catch (err) {
      setLoading(false);
      Swal.fire({
        ...swalConfig,
        title: "Error",
        text: "Something went wrong",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    if (ShowCreateAppointment) {
      SetAccSession({});
      setPartition(null);
      setBuilding(null);
      setIsDummy(false);
      setDummyData({
        patientName: "",
        sessionName: "",
        patientId: null,
        patientObj: null,
      });
      setLoading(false);
    }
  }, [ShowCreateAppointment]);

  useEffect(() => {
    if (isDummy) {
      setCreateIsReady(
        dummyData.patientName !== "" && dummyData.sessionName !== "",
      );
    } else {
      setCreateIsReady(
        Object.keys(AccSession).length > 0 && Object.keys(AppHour).length > 0,
      );
    }
  }, [AccSession, AppHour, isDummy, dummyData]);

  return (
    <Dialog
      id="appointment-dialog"
      open={ShowCreateAppointment}
      onClose={handleClose}
      fullWidth
      maxWidth="6000px"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f8f9fa",
        }}
      >
        <Box>
          <MDTypography variant="h5" fontWeight="bold">
            Create Appointment
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Therapist: {AppHour.therapistName} | ID: {AppHour.id}
          </MDTypography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Partition Selector */}
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  display="block"
                  mb={1}
                >
                  Select Partition
                </MDTypography>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  isClearable
                  loadOptions={loadPartitions}
                  value={partition}
                  onChange={setPartition}
                  placeholder="Choose Partition..."
                  styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  display="block"
                  mb={1}
                >
                  Select Building
                </MDTypography>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  isClearable
                  loadOptions={loadBuildings}
                  value={building}
                  onChange={setBuilding}
                  placeholder="Choose Building..."
                  styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Dummy Toggle */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isDummy}
                  onChange={(e) => setIsDummy(e.target.checked)}
                  color="info"
                />
              }
              label={<MDTypography variant="h6">Dummy Session</MDTypography>}
            />

            {isDummy && (
              <Stack
                direction="row"
                spacing={2}
                mt={2}
                sx={{
                  p: 2,
                  border: "1px dashed #3560AD",
                  borderRadius: 2,
                  bgcolor: "#f0f2f5",
                  alignItems: "flex-end",
                }}
              >
                <Box sx={{ width: "50%" }}>
                  <PatientSelector
                    value={dummyData.patientObj}
                    onChange={(val) =>
                      setDummyData({
                        ...dummyData,
                        patientName: val ? val.name : "",
                        patientId: val ? val.id : null,
                        patientObj: val,
                      })
                    }
                  />
                </Box>
                <Box sx={{ width: "50%" }}>
                  <SessionSelector
                    patient={dummyData.patientObj}
                    value={
                      dummyData.sessionName
                        ? { name: dummyData.sessionName }
                        : null
                    }
                    onChange={(val) =>
                      setDummyData({
                        ...dummyData,
                        sessionName: val ? val.name : "",
                      })
                    }
                  />
                </Box>
              </Stack>
            )}
          </Box>

          {/* Existing Session Status */}
          {!isDummy && (
            <MDBox p={2} bgColor="grey-100" borderRadius="lg">
              <MDTypography variant="caption" fontWeight="bold" color="text">
                Selected Session Status:
              </MDTypography>
              <MDTypography
                variant="h6"
                color={AccSession.id ? "success" : "error"}
              >
                {AccSession.id ? `ID: ${AccSession.id}` : "No Session Selected"}
              </MDTypography>
            </MDBox>
          )}
        </Stack>

        <Box
          sx={{
            opacity: isDummy ? 0.4 : 1,
            pointerEvents: isDummy ? "none" : "auto",
            mt: 3,
          }}
        >
          <MDTypography
            variant="button"
            fontWeight="bold"
            display="block"
            mb={1}
          >
            Choose Existing Session
          </MDTypography>
          <SingleSessionsApps
            key={ShowCreateAppointment ? `open-${AppHour?.id}` : "closed"}
            SetAccSession={SetAccSession}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: "#f8f9fa" }}>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="info"
          disabled={!createIsReady || loading}
          sx={{ px: 4, borderRadius: "8px" }}
        >
          {loading ? "Processing..." : "Create Appointment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAppointmentPopUp;
